import React, { useEffect, useRef, useState } from "react";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import "@tensorflow/tfjs";
import { tokens } from "../../public/tokens";

const ObjectDetection = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 640, height: 480 });

  useEffect(() => {
    let animationFrameId;
    
    const setupCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error('getUserMedia is not supported');
        return;
      }

      try {
        const model = await cocoSsd.load();

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: "user",
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          await new Promise((resolve) => {
            videoRef.current.onloadedmetadata = () => {
              setDimensions({
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              });
              
              if (canvasRef.current) {
                canvasRef.current.width = videoRef.current.videoWidth;
                canvasRef.current.height = videoRef.current.videoHeight;
              }
              
              resolve();
            };
          });
        }

        detectFrame(videoRef.current, model);
      } catch (error) {
        console.error('Error setting up camera:', error);
      }
    };

    const detectFrame = (video, model) => {
      if (!video || !model) return;
      
      if (!window.frameCounter) window.frameCounter = 0;
      window.frameCounter++;
      
      if (window.frameCounter % 5 === 0) {
        model.detect(video)
          .then((predictions) => {
            clearCanvas();
            
            const accuratePredictions = predictions.filter(prediction => {
              return prediction.class === "person" || prediction.class === "cell phone";
            });
            
            if (accuratePredictions.length > 0) {
              renderPredictions(accuratePredictions);
            }
          })
          .catch(console.error);
      }
      
      animationFrameId = requestAnimationFrame(() => detectFrame(video, model));
    };

    const clearCanvas = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    const renderPredictions = (predictions) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      const video = videoRef.current;
      
      if (!ctx || !canvas || !video) return;

      const videoWidth = video.videoWidth || dimensions.width;
      const videoHeight = video.videoHeight || dimensions.height;

      const container = canvas.parentElement;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scale to maintain aspect ratio (like object-fit: contain)
      const scale = Math.min(
        containerWidth / videoWidth,
        containerHeight / videoHeight
      );

      const scaledVideoWidth = videoWidth * scale;
      const scaledVideoHeight = videoHeight * scale;

      // Offsets (letterboxing)
      const offsetX = (containerWidth - scaledVideoWidth) / 2;
      const offsetY = (containerHeight - scaledVideoHeight) / 2;

      // Match canvas size to container
      canvas.width = containerWidth;
      canvas.height = containerHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ctx.font = "16px sans-serif";
      ctx.textBaseline = "top";

      predictions.forEach((prediction) => {
        const [x, y, width, height] = prediction.bbox;

        // Apply scaling + offset
        const scaledX = offsetX + x * scale;
        const scaledY = offsetY + y * scale;
        const scaledWidth = width * scale;
        const scaledHeight = height * scale;

        // Draw bounding box
        ctx.strokeStyle = tokens.colors.brand.red[500];
        ctx.lineWidth = 3;
        ctx.strokeRect(scaledX, scaledY, scaledWidth, scaledHeight);

        // Draw label
        ctx.fillStyle = tokens.colors.brand.red[500];
        const label = prediction.class;
        const textWidth = ctx.measureText(label).width;
        const textHeight = 16;
        ctx.fillRect(scaledX, scaledY - textHeight - 4, textWidth + 8, textHeight + 8);

        ctx.fillStyle = tokens.colors.neutral.black;
        ctx.fillText(label, scaledX + 4, scaledY - textHeight);
      });
    };

    setupCamera();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return (
    <div className="object-detection-con">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
      
      />
      <canvas
        ref={canvasRef}
      />
    </div>
  );
};

export default ObjectDetection;